// Catálogo tipado de eventos de ec-bo (EC-3811).
//
// Alcance Fase 1: identidad/sesión, navegación y filtro de Company Client.
// Alcance Fase 2 (en curso): interacciones de los dashboards del PortalB2B
// —filtros de período y de versión de app, apertura de drilldowns—, de los
// listados —búsqueda, paginado y export— y el resultado de los 5 flujos de alta.
//
// NINGÚN evento declara `screen` ni `subview`: el core los adjunta solo a partir
// del último page(). Ver currentScreen en core.ts. Un componente hoja no sabe en
// qué solapa está —los paneles del PortalB2B no están en la URL— así que pedirle
// ese dato al call site era la forma segura de que llegara mal.
//
// Este archivo contiene los DOS gates de privacidad en tiempo de compilación:
//   GATE 1 — ninguna prop de evento puede llamarse como algo PII.
//   GATE 2 — el allowlist runtime cubre todas las claves declaradas.
// Si alguno se rompe, `tsc` falla y nombra el problema.
import type { AnalyticsContext, IdentityTraits } from "./types";
import type { ScreenName } from "./screens";

export interface AnalyticsEventMap {
  // ── Navegación (los emite el core vía page(); no llamar a mano) ────────────
  screen_viewed: {
    screen: ScreenName;
    /** Solapa o subvista que NO está en la URL (PortalB2B, Stock & Envíos). */
    subview?: string | null;
    /** Ruta con los identificadores reemplazados. Nunca la URL cruda. */
    path_shape?: string;
  };

  // ── Sesión ────────────────────────────────────────────────────────────────
  login_succeeded: {
    method: "password" | "google" | "apple" | "sso_bridge" | "restored";
  };
  login_failed: {
    method: "password" | "google" | "apple" | "sso_bridge";
    reason: "bad_credentials" | "network" | "unknown";
  };
  session_expired: {
    source: "interceptor_401" | "boot_check";
  };
  logged_out: {
    reason: "user" | "session_expired";
  };

  // ── Tenant ────────────────────────────────────────────────────────────────
  company_client_filter_changed: {
    /** Forma de la selección resultante. Ver AnalyticsContext. */
    selection: "all" | "single" | "multi" | "none";
    /** Cuántos CC quedaron seleccionados. null si quedó en "Todos". */
    count: number | null;
    /** true si algún CC se marcó como "solo el padre" (EC-3797). */
    has_self_only: boolean;
  };

  // ── Interacciones de los dashboards del PortalB2B (Fase 2) ────────────────
  /**
   * Cambió el rango temporal del dashboard. Sale de los tres controles que lo
   * pueden mover: el atajo y los dos DatePicker de "Personalizado".
   *
   * `range_days` es lo que hace útil al evento: dice si el operador mira una
   * semana o noventa días, que es el insumo para decidir defaults y para saber
   * si el tope de 90 días de EC-3685 molesta en la práctica.
   */
  dashboard_period_changed: {
    period: DashboardPeriod;
    /** Qué control lo movió. `preset` filtra el ruido de los dos picks seguidos. */
    source: "preset" | "custom_start" | "custom_end";
    /**
     * Span en días entre inicio y fin, sin sumar los extremos: el preset
     * "Últimos 30 días" reporta 30. Coincide con la etiqueta que vio el
     * operador, no con los días calendario que cubre ec-be (uno más, porque su
     * `to` es inclusivo).
     */
    range_days: number | null;
  };

  /**
   * Cambió el filtro de versión de app. Es multi-selección; 0 significa "Todas".
   * No viajan las versiones elegidas, sólo cuántas: el objetivo es saber si el
   * filtro se usa, no reconstruir el parque de relojes desde analytics.
   */
  dashboard_app_version_filter_changed: {
    version_count: number;
  };

  /**
   * Se abrió un modal de drilldown. Mide qué tarjeta del dashboard genera
   * interés real, que es distinto de qué tarjeta se ve.
   *
   * NO viajan los filtros internos del modal ni el paciente al que se llegó: el
   * drilldown lista personas y cualquier parámetro de ahí es dato clínico.
   */
  drilldown_opened: {
    card: DrilldownCard;
  };

  // ── Listados (Fase 2, tramo 2) ────────────────────────────────────────────
  /**
   * Una búsqueda que se RESOLVIÓ en un listado.
   *
   * ⚠️ EL TÉRMINO BUSCADO NO VIAJA, Y NO PUEDE VIAJAR NUNCA. Los placeholders de
   * internacionalization/searchers.ts dicen exactamente qué escribe el operador
   * ahí: nombre y apellido del adulto mayor, email, DNI, IMEI, número de serie.
   * Es PII directa, y en un sistema de salud el hecho de que alguien buscara a
   * una persona ya es información sobre esa persona.
   *
   * Lo que viaja es la FORMA de la búsqueda, que alcanza para las preguntas que
   * importan sin poder reconstruir a quién se buscó.
   *
   * `has_results: false` es la señal más valiosa de todo el tramo: es un
   * operador que no encontró lo que necesitaba. Un pico ahí es un problema de
   * producto —falta un campo indexado, el listado filtra de más, el dato no
   * está— y hoy no hay forma de enterarse.
   */
  listing_searched: {
    listing: ListingName;
    /**
     * Tramo de longitud, no la longitud exacta: distingue "tanteó" de "pegó un
     * valor completo" sin dar un número que, cruzado con hora y CC, pudiera
     * acercarse a identificar el término.
     */
    term_length_bucket: TermLengthBucket;
    /**
     * Qué CLASE de dato se escribió, nunca el dato. Dice si los operadores
     * buscan por identificador (DNI, IMEI, línea) o por nombre, que es lo que
     * define qué conviene indexar en ec-be.
     */
    term_kind: "numeric" | "alpha" | "mixed";
    has_results: boolean;
    /** Cuántos resultados. Un número alto sostenido significa que no discrimina. */
    result_count: number | null;
  };

  /**
   * El operador pasó de página. Si casi nadie lo hace, el orden por defecto del
   * listado es lo único que importa y conviene invertir ahí; si todos paginan,
   * falta un filtro.
   */
  listing_paginated: {
    listing: ListingName;
    page: number;
  };

  /**
   * Se descargó un listado. `row_count` dice si se exporta un recorte o la base
   * entera, que es una diferencia de riesgo y no sólo de uso: un CSV con la base
   * completa de una red sale del perímetro de la aplicación.
   */
  // ── Flujos de alta (Fase 2, tramo 3) ──────────────────────────────────────
  /**
   * El operador abrió un formulario de alta.
   *
   * Es el denominador del embudo, y por eso no alcanza con el pageview de la
   * pantalla: dos de los cinco flujos viven en un modal (alta de acompañante,
   * apareo) y no cambian la URL, así que no tienen pageview propio. Con este
   * evento el embudo se sostiene solo y es igual para los cinco.
   */
  flow_started: {
    flow: FlowName;
  };

  /**
   * El hecho de negocio ocurrió: el envío se creó, el reloj quedó apareado.
   *
   * Es la diferencia entre "llegó a la última pantalla" y "terminó el trabajo".
   * Un embudo hecho sólo con pageviews confunde las dos cosas.
   */
  flow_completed: {
    flow: FlowName;
  };

  /**
   * El intento falló. ESTE es el evento que faltaba.
   *
   * Sin él, abandonar y fallar son indistinguibles: el operador que se rinde en
   * el paso 3 y el que aprieta Guardar y recibe un 400 producen exactamente los
   * mismos eventos —ninguno—, así que el embudo muestra la misma caída para dos
   * problemas opuestos. Uno se arregla con diseño y el otro con código, y hasta
   * ahora no había forma de saber cuál era cuál.
   *
   * `reason` es lo que hace accionable la caída. NO viaja el mensaje de error:
   * los de ec-be pueden nombrar al paciente.
   */
  flow_failed: {
    flow: FlowName;
    reason: FlowFailureReason;
  };

  listing_exported: {
    listing: ListingName;
    /**
     * `filtered` es el recorte que el operador está viendo; `full` es la base
     * entera del listado. Se distinguen porque no son el mismo hecho: un CSV con
     * la base completa de una red sale del perímetro de la aplicación y deja de
     * estar bajo ningún control de acceso.
     */
    scope: "filtered" | "full";
    /** null cuando no se conoce al momento del click (export de la base entera). */
    row_count: number | null;
  };
}

/** Listados con instrumentación. Cerrado a mano: refleja lo cableado, no lo deseable. */
export type ListingName =
  | "users"
  | "support_network"
  | "phone_lines"
  | "health_systems"
  | "subscribers";

export type TermLengthBucket = "1-2" | "3-5" | "6-10" | "11+";

/** Flujos de alta con resultado instrumentado. Cerrado a mano. */
export type FlowName =
  | "shipment_create"
  | "smartwatch_pair"
  | "companion_create"
  | "company_client_create"
  | "subscription_create";

/**
 * Por qué falló, en categorías que mapean a una acción distinta:
 *   validation → el formulario o ec-be rechazaron los datos. Suele ser UX: el
 *                campo no avisa lo que hace falta.
 *   permission → el rol no alcanza. Es un problema de gating o de expectativa.
 *   network    → no hubo respuesta. Conectividad del operador.
 *   server     → 5xx. Es un bug nuestro y va a un ticket.
 *   unknown    → no se pudo clasificar. Si crece, falta una categoría.
 */
export type FlowFailureReason =
  | "validation"
  | "permission"
  | "network"
  | "server"
  | "unknown";

/** Atajos del selector de período. `especific_dates` es un typo histórico vivo. */
export type DashboardPeriod =
  | "last_week"
  | "last_month"
  | "last_90_days"
  | "specific_dates"
  | "especific_dates";

/** Tarjetas con drilldown. Cerrado a mano: un string libre acá sería PII latente. */
export type DrilldownCard =
  | "low_battery"
  | "heart_rate"
  | "multiple_falls"
  | "high_alerts"
  | "alert_classification"
  | "user_management";

export type AnalyticsEventName = keyof AnalyticsEventMap;

// ─────────────────────────────────────────────────────────────────────────────
// GATE 1 — nombres de propiedad prohibidos
// ─────────────────────────────────────────────────────────────────────────────
type ForbiddenKey =
  | "email"
  | "mail"
  | "first_name"
  | "last_name"
  | "full_name"
  | "name"
  | "document_number"
  | "document_type"
  | "dni"
  | "cuil"
  | "cuit"
  | "phone"
  | "phone_number"
  | "whatsapp"
  | "lat"
  | "lng"
  | "long"
  | "latitude"
  | "longitude"
  | "address"
  | "birth_date"
  | "age"
  | "gender"
  | "observations"
  | "comment"
  | "notes"
  | "health_system_data"
  | "health_system_plan"
  | "health_system_affiliate_number"
  | "trigger_value"
  | "heart_rate"
  | "bpm"
  | "code_word"
  | "keyword"
  | "token"
  | "jwt"
  | "password"
  | "patient_id"
  | "imei"
  | "serial"
  | "url"
  | "referrer";

/** Devuelve los nombres de evento que declaran alguna clave prohibida. */
type EventsWithForbiddenKeys<M> = {
  [E in keyof M]: Extract<keyof M[E], ForbiddenKey> extends never ? never : E;
}[keyof M];

/**
 * Si algún evento declara una clave prohibida, este tipo pasa a ser `never` y la
 * asignación de abajo NO compila. El mensaje de error nombra el archivo; el
 * evento culpable se encuentra inspeccionando `EventsWithForbiddenKeys`.
 */
type NoForbiddenKeys<M> = EventsWithForbiddenKeys<M> extends never ? true : never;

export const ANALYTICS_PII_GATE: NoForbiddenKeys<AnalyticsEventMap> = true;

// ─────────────────────────────────────────────────────────────────────────────
// GATE 2 — allowlist runtime derivado del tipo
// ─────────────────────────────────────────────────────────────────────────────
type EventPropKeys = {
  [E in keyof AnalyticsEventMap]: keyof AnalyticsEventMap[E];
}[keyof AnalyticsEventMap];

type AllDeclaredKeys = EventPropKeys | keyof AnalyticsContext | keyof IdentityTraits;

/**
 * Agregar una propiedad nueva a AnalyticsEventMap sin agregarla acá es un error
 * de compilación (falta la clave en el Record). Agregar acá una clave que no
 * existe en ningún evento también lo es. El allowlist no puede quedar desfasado.
 */
const ALLOW: Record<Extract<AllDeclaredKeys, string>, true> = {
  // screen_viewed
  screen: true,
  subview: true,
  path_shape: true,
  // sesión
  method: true,
  reason: true,
  source: true,
  // flujos de alta (Fase 2, tramo 3)
  flow: true,
  // listados (Fase 2, tramo 2)
  listing: true,
  term_length_bucket: true,
  term_kind: true,
  has_results: true,
  result_count: true,
  scope: true,
  page: true,
  row_count: true,
  // interacciones de dashboard (Fase 2)
  period: true,
  range_days: true,
  version_count: true,
  card: true,
  // tenant
  selection: true,
  count: true,
  has_self_only: true,
  company_client_selection: true,
  company_client_count: true,
  // super properties (AnalyticsContext)
  portal: true,
  environment: true,
  app_version: true,
  locale: true,
  role: true,
  company_client_id: true,
  company_client_scope: true,
  is_ellie_care_general: true,
  is_impersonating: true,
  // identify (IdentityTraits)
  home_company_client_id: true,
};

/** El allowlist que consume el sanitizador. */
export const ALLOW_KEYS: ReadonlySet<string> = new Set(Object.keys(ALLOW));
